import {
  Button,
  Field,
  Input,
  Label,
  Textarea,
  Select,
} from "@headlessui/react";
import clsx from "clsx";
import React, { useState, useEffect } from "react";
import FormularioCuestionario from "./FormularioCuestionario";
import PreviewContrato from "./PreviewContrato";
import {
  LaptopIcon,
  Smartphone,
  FileText,
  ClipboardList,
  Plus,
  Minus,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  X,
  Settings,
  Edit3,
  PlusCircle,
  Trash2,
} from "lucide-react";
import ModalConfimacion from "./ModalConfimacion";
import { DeviceFrameset } from "react-device-frameset";
import "react-device-frameset/styles/marvel-devices.min.css";
import { useNotifications } from "../../contexts/NotificationContext";
import { useSelectedEvent } from "../../contexts/SelectedEventContext";

export default function CrearCuestionarioPages({ onClose }) {
  const { showSuccess, showError } = useNotifications();
  const { eventoActual } = useSelectedEvent(); // removido isLoading no usado

  // Estado para lugares
  const [lugares, setLugares] = useState([]);
  const [isLoadingLugares, setIsLoadingLugares] = useState(false);

  // Estado del formulario inicializado con datos del evento
  const [formData, setFormData] = useState({
    nombreEvento: "",
    descripcionEvento: "",
    lugar_id: "",
    fechaHoraEvento: "",
    nombreCompleto: "",
    carreraEstudios: "",
    escuelaInstitucion: "",
    cantidadBoletos: "",
    contactoEmergencia: "",
    tutorNombre: "",
    tutorApellidoPaterno: "",
    tutorApellidoMaterno: "",
  });

  // Estado para restricciones alimenticias
  const [restricciones, setRestricciones] = useState({
    vegetariano: 0,
    vegano: 0,
    sinGluten: 0,
    alergiaMarisco: 0,
  });

  const [otra, setOtra] = useState("");
  const [modoVista, setModoVista] = useState("laptop"); // "laptop" o "telefono"
  const [vistaPrevia, setVistaPrevia] = useState("cuestionario"); // "cuestionario" o "contrato"
  const [modalOpen, setModalOpen] = useState(false);
  const [Ocultar, setOcultar] = useState(true);

  // Estado para configuración dinámica del formulario
  const CAMPOS_BASE_DEFAULT = [
    { id: "nombre", label: "Nombre (s)", editable: true },
    { id: "apellidoPaterno", label: "Apellido paterno", editable: true },
    { id: "apellidoMaterno", label: "Apellido materno", editable: true },
    { id: "fechaNacimiento", label: "Fecha de nacimiento", editable: true },
    { id: "correo", label: "Correo electrónico personal", editable: true },
    { id: "licenciatura", label: "Licenciatura o especialidad", editable: true },
    { id: "boletos", label: "Número aproximado de boletos a solicitar con graduado incluido", editable: true },
    { id: "telefono", label: "Número de teléfono (WhatsApp)", editable: true },
    { id: "requiere_tutor", label: "Datos del tutor o responsable", editable: true },
    { id: "tutor_nombre", label: "Nombre (s) del tutor", editable: true },
    { id: "tutor_apellidoPaterno", label: "Apellido paterno del tutor", editable: true },
    { id: "tutor_apellidoMaterno", label: "Apellido materno del tutor", editable: true },
    { id: "tutor_correo", label: "Correo electrónico del tutor", editable: true },
    { id: "tutor_fechaNacimiento", label: "Fecha de nacimiento del tutor", editable: true },
    { id: "tutor_telefono", label: "Teléfono del tutor", editable: true },
  ];

  const [configuracionCuestionario, setConfiguracionCuestionario] = useState(() => {
    const inicial = eventoActual?.configuracion_cuestionario;
    return {
      campos_base: inicial?.campos_base || CAMPOS_BASE_DEFAULT,
      campos_extra: inicial?.campos_extra || [],
    };
  });

  const [nuevoCampo, setNuevoCampo] = useState({ label: "", tipo: "text", requerido: false, seccion: "graduado" });
  const [opcionesNuevoCampo, setOpcionesNuevoCampo] = useState([]);
  const [opcionActual, setOpcionActual] = useState("");
  const [guardandoConfig, setGuardandoConfig] = useState(false);

  // Efecto para limpiar opciones del nuevo campo si cambia de tipo
  useEffect(() => {
    if (nuevoCampo.tipo !== "select") {
      setOpcionesNuevoCampo([]);
      setOpcionActual("");
    }
  }, [nuevoCampo.tipo]);

  // Efecto para cargar lugares
  useEffect(() => {
    const cargarLugares = async () => {
      setIsLoadingLugares(true);
      try {
        const eventService = (await import("../../services/eventService"))
          .default;
        const resultado = await eventService.getLugares();

        if (resultado.success) {
          setLugares(resultado.data);
        } else {
          console.error("❌ Error al cargar lugares:", resultado.error);
        }
      } catch (error) {
        console.error("❌ Error inesperado al cargar lugares:", error);
      } finally {
        setIsLoadingLugares(false);
      }
    };

    cargarLugares();
  }, []);

  // Efecto para cargar datos del evento seleccionado
  useEffect(() => {
    if (eventoActual) {
      setFormData((prev) => ({
        ...prev,
        nombreEvento: eventoActual.nombreEvento || "",
        descripcionEvento: eventoActual.descripcionEvento || "",
        lugar_id: eventoActual.lugar_id || eventoActual.lugar?.id || "",
        fechaHoraEvento: eventoActual.fechaHora || "",
        escuelaInstitucion: eventoActual.nombreInstitucion || "",
      }));
      // Cargar configuración del formulario desde el evento
      if (eventoActual.configuracion_cuestionario) {
        const cfg = eventoActual.configuracion_cuestionario;
        setConfiguracionCuestionario({
          campos_base: cfg.campos_base?.length > 0 ? cfg.campos_base : CAMPOS_BASE_DEFAULT,
          campos_extra: cfg.campos_extra || [],
        });
      }
    }
  }, [eventoActual]);

  // Función para manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Función para copiar enlace usando el ID del evento seleccionado
  const handleCopyLink = () => {
    const eventId = eventoActual?.id || "mg07vfc5ma7io4axa"; // fallback al ID original
    const link = `${window.location.origin}/cuestionario/${eventId}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        showSuccess("Enlace copiado");
      })
      .catch((err) => {
        showError("Error al copiar el enlace");
      });
  };

  const handleChange = (key, delta) => {
    setRestricciones((prev) => ({
      ...prev,
      [key]: Math.max(0, prev[key] + delta),
    }));
  };

  // Funciones para editar configuración del cuestionario
  const handleEditarLabelBase = (id, nuevoLabel) => {
    setConfiguracionCuestionario((prev) => ({
      ...prev,
      campos_base: prev.campos_base.map((c) =>
        c.id === id ? { ...c, label: nuevoLabel } : c
      ),
    }));
  };

  const handleAgregarCampoExtra = () => {
    if (!nuevoCampo.label.trim()) return;
    if (nuevoCampo.tipo === "select" && opcionesNuevoCampo.length === 0) {
      showError("Debes agregar al menos una opción para el menú de selección");
      return;
    }
    const id = nuevoCampo.label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    
    if (configuracionCuestionario.campos_extra.some(c => c.id === id)) {
      showError("Ya existe un campo con este nombre o similar");
      return;
    }

    const campo = {
      id,
      ...nuevoCampo,
      label: nuevoCampo.label.trim(),
      opciones: nuevoCampo.tipo === "select" ? opcionesNuevoCampo : undefined,
    };

    setConfiguracionCuestionario((prev) => ({
      ...prev,
      campos_extra: [...prev.campos_extra, campo],
    }));
    setNuevoCampo({ label: "", tipo: "text", requerido: false, seccion: "graduado" });
    setOpcionesNuevoCampo([]);
    setOpcionActual("");
  };

  const handleEliminarCampoExtra = (id) => {
    setConfiguracionCuestionario((prev) => ({
      ...prev,
      campos_extra: prev.campos_extra.filter((c) => c.id !== id),
    }));
  };

  const handleAgregarOpcionNuevoCampo = () => {
    if (!opcionActual.trim()) return;
    if (opcionesNuevoCampo.includes(opcionActual.trim())) {
      setOpcionActual("");
      return;
    }
    setOpcionesNuevoCampo((prev) => [...prev, opcionActual.trim()]);
    setOpcionActual("");
  };

  const handleEliminarOpcionNuevoCampo = (index) => {
    setOpcionesNuevoCampo((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGuardarConfiguracion = async (mostrarConfirmacion = false) => {
    if (!eventoActual?.id) {
      showError("No hay evento seleccionado");
      return;
    }
    setGuardandoConfig(true);
    try {
      const eventService = (await import("../../services/eventService")).default;
      const resultado = await eventService.updateEventConfiguracion(
        eventoActual.id,
        configuracionCuestionario
      );
      if (resultado.success) {
        showSuccess("Configuración del formulario guardada");
        if (mostrarConfirmacion) {
          setModalOpen(true);
        }
      } else {
        showError("Error al guardar la configuración");
      }
    } catch (error) {
      console.error("Error al guardar configuración:", error);
      showError("Error al guardar la configuración");
    } finally {
      setGuardandoConfig(false);
    }
  };

  return (
    <div className="relative rounded-xl border-2 bg-white dark:bg-fodoBlack  shadow-md">
      <button
        className=" absolute -top-4 -right-4 z-20 px-2 py-2  rounded-full bg-casal text-white font-semibold"
        onClick={onClose}
      >
        <X></X>
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-3 md:gap-0">
        <div className={clsx("lg:col-span-1 relative flex flex-col", Ocultar && "hidden")}>
          <div className="flex-1 h-full lg:h-[72vh] mb-16 md:mb-16 lg:mb-16 overflow-y-auto pr-2">
            {/* <div className="p-4"> */}
              {/* <h2 className="text-lg font-semibold  text-casal dark:text-Acapulco mb-0">
                Crear invitación de cuestionario
              </h2>
              <p className="text-gray-500 dark:text-gray-200 mb-4">
                Completa los campos y envía enlace de tu evento.
              </p> */}

              {/* Mostrar información del evento seleccionado */}
              {/* {eventoActual && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                  📅 Evento seleccionado: <span className="font-semibold">{eventoActual.nombre_evento}</span>
                </p>
                {eventoActual.fechaHora && (
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                    {eventoActual.fechaHora}
                  </p>
                )}
              </div>
            )} */}

              {/* <p className="text-gray-800 dark:text-gray-200 mb-4 text-lg font-semibold">
                Datos del evento
              </p>
              <div>
                <Field>
                  <div className="mb-3">
                    <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                      Nombre del evento
                    </Label>
                    <Input
                      type="text"
                      value={formData.nombreEvento}
                      onChange={(e) =>
                        handleInputChange("nombreEvento", e.target.value)
                      }
                      className={clsx(
                        "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                        "placeholder:italic",
                        "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                      )}
                      placeholder="Ejemplo: Ceremonia de Graduación - Generación 2025"
                    />
                  </div>
                  <div className="mb-3">
                    <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                      Descripción del evento
                    </Label>
                    <Textarea
                      value={formData.descripcionEvento}
                      onChange={(e) =>
                        handleInputChange("descripcionEvento", e.target.value)
                      }
                      className={clsx(
                        "mt-2 block w-full rounded-lg border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                        "placeholder:italic",
                        "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                      )}
                      rows={4}
                      placeholder="Ejemplo: ¡Felicidades por tu próxima graduación! Por favor completa este formulario con tus datos. Esta información nos ayudará a organizar de la mejor forma el evento, asignar tus boletos y tomar en cuenta tus necesidades."
                    />
                  </div>
                  <div className="mb-3">
                    <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                      Lugar de evento
                    </Label>
                    <div className="relative">
                      {isLoadingLugares ? (
                        <div className="mt-2 w-full h-9 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-3xl"></div>
                      ) : (
                        <>
                          <Select
                            value={formData.lugar_id}
                            onChange={(e) =>
                              handleInputChange("lugar_id", e.target.value)
                            }
                            disabled={isLoadingLugares}
                            className={clsx(
                              "mt-2 block w-full appearance-none rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                              "placeholder:italic",
                              "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent",
                              "*:text-black",
                              "disabled:opacity-50 disabled:cursor-not-allowed"
                            )}
                          >
                            <option value="">Seleccionar lugar</option>
                            {lugares.map((lugar) => (
                              <option key={lugar.id} value={lugar.id}>
                                {lugar.nombre}
                              </option>
                            ))}
                          </Select>
                          <ChevronDown
                            className="group pointer-events-none absolute top-4 right-3 size-4 fill-black/60 dark:fill-white/60"
                            aria-hidden="true"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mb-3">
                    <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                      Fecha y hora del evento
                    </Label>
                    <Input
                      type="datetime-local"
                      value={formData.fechaHoraEvento}
                      onChange={(e) =>
                        handleInputChange("fechaHoraEvento", e.target.value)
                      }
                      className={clsx(
                        "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                        "placeholder:italic",
                        "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                      )}
                      placeholder="Ejemplo: 25 de junio de 2025 - 19:00 hrs"
                    />
                  </div>
                </Field>
              </div> */}
            {/* </div> */}

            {/* Sección de configuración del formulario */}
            <div className="p-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border-t-2 border-gray-100 dark:border-gray-700 rounded-b-xl lg:rounded-b-none relative overflow-hidden shadow-sm">
              <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <Settings className="w-24 h-24 text-casal" />
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-2 bg-casal/10 rounded-lg">
                    <Settings className="w-5 h-5 text-casal dark:text-Acapulco" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                    Configuración del Cuestionario
                  </h3>
                </div>

                {/* Campos base */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Edit3 className="w-4 h-4 text-casal" />
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Etiquetas de campos base
                    </h4>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 ml-6">Personaliza el texto que verán tus invitados para los datos estándar.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {configuracionCuestionario.campos_base.map((campo) => {
                      const esLicenciatura = campo.id === 'licenciatura';
                      const numLicenciaturas = eventoActual?.licenciatura?.split(",").filter(l=>l.trim()).length || 0;
                      if (esLicenciatura && numLicenciaturas <= 1) return null;

                      const esTutor = campo.id === 'requiere_tutor' || campo.id.startsWith('tutor_');
                      if (esTutor && !eventoActual?.requiere_tutor) return null;

                      return (
                        <div key={campo.id} className="group flex flex-col bg-white dark:bg-gray-800/80 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-casal/30 transition-all duration-300">
                          <label className="text-[10px] text-gray-400 font-bold mb-1 uppercase tracking-wider pl-1">
                            {campo.id.replace(/_/g, ' ')}
                          </label>
                          <input
                            type="text"
                            value={campo.label}
                            onChange={(e) => handleEditarLabelBase(campo.id, e.target.value)}
                            className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-casal px-1 py-1 text-sm font-medium text-gray-800 dark:text-gray-100 focus:outline-none transition-colors"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Campos extra */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <PlusCircle className="w-4 h-4 text-casal" />
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Campos adicionales personalizados
                    </h4>
                  </div>

                  {configuracionCuestionario.campos_extra.length > 0 && (
                    <div className="space-y-3 mb-5">
                      {configuracionCuestionario.campos_extra.map((campo) => (
                        <div key={campo.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm group hover:border-casal/30 transition-all">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-gray-800 dark:text-gray-100">{campo.label}</span>
                              {campo.requerido && (
                                <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[10px] font-bold uppercase tracking-wider">Obligatorio</span>
                              )}
                              {campo.seccion === 'tutor' && (
                                <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[10px] font-bold uppercase tracking-wider">Tutor</span>
                              )}
                            </div>
                             <span className="text-xs text-gray-500 mt-0.5">
                              Tipo de respuesta:{" "}
                              {campo.tipo === "number"
                                ? "Número"
                                : campo.tipo === "date"
                                ? "Fecha"
                                : campo.tipo === "time"
                                ? "Hora"
                                : campo.tipo === "textarea"
                                ? "Párrafo (Texto largo)"
                                : campo.tipo === "select"
                                ? `Selección (${campo.opciones?.join(", ") || ""})`
                                : "Texto corto"}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleEliminarCampoExtra(campo.id)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Eliminar campo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Agregar nuevo campo */}
                  <div className="bg-casal/5 dark:bg-gray-800/50 border border-casal/20 dark:border-gray-700 rounded-2xl p-4 transition-all">
                    <p className="text-[11px] font-bold text-casal dark:text-Acapulco mb-3 uppercase tracking-wider">Añadir nuevo campo</p>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={nuevoCampo.label}
                        onChange={(e) => setNuevoCampo((p) => ({ ...p, label: e.target.value }))}
                        placeholder="Pregunta o nombre del campo (ej: Talla de playera)"
                        className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:border-casal focus:ring-1 focus:ring-casal transition-all"
                      />
                      
                      <div className="flex flex-wrap gap-2.5 items-stretch">
                        <div className="relative flex-1 min-w-[120px]">
                          <select
                            value={nuevoCampo.tipo}
                            onChange={(e) => setNuevoCampo((p) => ({ ...p, tipo: e.target.value }))}
                            className="w-full appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl pl-3 pr-8 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:border-casal focus:ring-1 focus:ring-casal transition-all"
                          >
                             <option value="text">Texto corto</option>
                            <option value="textarea">Párrafo (Texto largo)</option>
                            <option value="number">Número</option>
                            <option value="date">Fecha</option>
                            <option value="time">Hora</option>
                            <option value="select">Selección (Menú desplegable)</option>
                           </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>

                        {nuevoCampo.tipo === "select" && (
                          <div className="w-full space-y-2 mt-2 bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                            <p className="text-xs font-bold text-casal dark:text-Acapulco uppercase">Opciones del menú</p>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={opcionActual}
                                onChange={(e) => setOpcionActual(e.target.value)}
                                placeholder="Añadir opción (ej: Soltero)"
                                className="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-casal focus:border-casal text-gray-800 dark:text-gray-100"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAgregarOpcionNuevoCampo();
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={handleAgregarOpcionNuevoCampo}
                                className="bg-casal text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-Acapulco transition-all"
                              >
                                Añadir
                              </button>
                            </div>
                            {opcionesNuevoCampo.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {opcionesNuevoCampo.map((opc, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center gap-1 bg-casal/10 text-casal dark:text-Acapulco text-xs px-2.5 py-1 rounded-full border border-casal/20"
                                  >
                                    {opc}
                                    <button
                                      type="button"
                                      onClick={() => handleEliminarOpcionNuevoCampo(idx)}
                                      className="text-casal hover:text-red-500 font-bold"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-[11px] text-gray-400 italic">Agrega al menos una opción para el menú desplegable.</p>
                            )}
                          </div>
                        )}

                        {eventoActual?.requiere_tutor && (
                          <div className="relative flex-1 min-w-[120px]">
                            <select
                              value={nuevoCampo.seccion}
                              onChange={(e) => setNuevoCampo((p) => ({ ...p, seccion: e.target.value }))}
                              className="w-full appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl pl-3 pr-8 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:border-casal focus:ring-1 focus:ring-casal transition-all"
                            >
                              <option value="graduado">Para graduado</option>
                              <option value="tutor">Para tutor</option>
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          </div>
                        )}
                        
                        <label className="flex items-center justify-center flex-1 min-w-[100px] gap-2 cursor-pointer bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-2 rounded-xl transition-all hover:border-casal/50">
                          <input
                            type="checkbox"
                            checked={nuevoCampo.requerido}
                            onChange={(e) => setNuevoCampo((p) => ({ ...p, requerido: e.target.checked }))}
                            className="w-4 h-4 text-casal rounded border-gray-300 focus:ring-casal"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Obligatorio</span>
                        </label>
                        
                        <button
                          type="button"
                          onClick={handleAgregarCampoExtra}
                          disabled={!nuevoCampo.label.trim()}
                          className="flex-1 min-w-[80px] flex items-center justify-center gap-1.5 bg-casal text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-Acapulco transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:hover:bg-casal disabled:shadow-none"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Añadir</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGuardarConfiguracion}
                  disabled={guardandoConfig || !eventoActual?.id}
                  className="w-full mt-4 relative overflow-hidden group bg-casal text-white py-3 px-6 rounded-2xl text-sm font-bold shadow-lg shadow-casal/20 hover:shadow-casal/40 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-70 disabled:hover:translate-y-0 disabled:shadow-none"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {guardandoConfig ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Guardando configuración...
                      </span>
                    ) : (
                      "Guardar configuración"
                    )}
                  </span>
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out z-0"></div>
                </button>
              </div>
            </div>

            {/* <div className="p-4 space-y-4">
              <p className="text-gray-800 dark:text-gray-200 mb-4 text-lg font-semibold">
                Datos de asistentes
              </p>
              <Field>
                <div className="mb-3">
                  <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                    Nombre completo
                  </Label>
                  <Input
                    type="text"
                    value={formData.nombreCompleto}
                    onChange={(e) =>
                      handleInputChange("nombreCompleto", e.target.value)
                    }
                    className={clsx(
                      "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                      "placeholder:italic",
                      "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                    )}
                    placeholder="Respuesta"
                  />
                </div>
                <div className="mb-3">
                  <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                    Carrera o estudios realizados
                  </Label>
                  <Input
                    type="text"
                    value={formData.carreraEstudios}
                    onChange={(e) =>
                      handleInputChange("carreraEstudios", e.target.value)
                    }
                    className={clsx(
                      "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                      "placeholder:italic",
                      "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                    )}
                    placeholder="Respuesta"
                  />
                </div>
                <div className="mb-3">
                  <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                    Escuela o institución
                  </Label>
                  <Input
                    type="text"
                    value={formData.escuelaInstitucion}
                    onChange={(e) =>
                      handleInputChange("escuelaInstitucion", e.target.value)
                    }
                    className={clsx(
                      "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                      "placeholder:italic",
                      "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                    )}
                    placeholder="Respuesta"
                  />
                </div>
                <div className="mb-3">
                  <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                    Cantidad de boletos requeridos
                  </Label>
                  <Input
                    type="number"
                    value={formData.cantidadBoletos}
                    onChange={(e) =>
                      handleInputChange("cantidadBoletos", e.target.value)
                    }
                    className={clsx(
                      "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                      "placeholder:italic",
                      "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                    )}
                    placeholder="6 Boletos"
                  />
                </div>
                <div className="mb-3">
                  <Label className="text-sm/6">
                    <span className="text-casal font-semibold dark:text-towerGray">
                      Restricciones alimenticias
                    </span>{" "}
                    <span className="text-gray-800 dark:text-gray-200">
                      {" "}
                      Ejemplo: vegetarianos, vegano, sin gluten, alergias a
                      mariscos, etc
                    </span>
                  </Label>
                  <div className="p-4 border-2 rounded-2xl bg-transparent border-[#bcd6e4]">
                    {[
                      { label: "Vegetariano", key: "vegetariano" },
                      { label: "Vegano", key: "vegano" },
                      { label: "Sin gluten", key: "sinGluten" },
                      { label: "Alergia a marisco", key: "alergiaMarisco" },
                    ].map((item) => (
                      <div
                        key={item.key}
                        className="flex items-center justify-between mb-2"
                      >
                        <span className="text-gray-600 dark:text-gray-300 font-medium w-40">
                          {item.label}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="w-6 h-6 rounded-full border border-gray-400 text-gray-600 dark:text-gray-300 flex items-center justify-center hover:bg-gray-100"
                            onClick={() => handleChange(item.key, -1)}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className="w-6 h-6 rounded-full border border-gray-400 text-gray-600 dark:text-gray-300 flex items-center justify-center hover:bg-gray-100"
                            onClick={() => handleChange(item.key, 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-gray-600 dark:text-gray-300 w-24 text-right">
                          {restricciones[item.key]} personas
                        </span>
                      </div>
                    ))}
                    <div className="mt-3 mb-1 text-gray-600 dark:text-gray-300 font-medium">
                      Añadir una restricción específica
                    </div>
                    <input
                      type="text"
                      value={otra}
                      onChange={(e) => setOtra(e.target.value)}
                      placeholder="Respuesta"
                      className={clsx(
                        "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                        "placeholder:italic",
                        "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                      )}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                    Contacto de emergencia
                  </Label>
                  <Input
                    type="text"
                    value={formData.contactoEmergencia}
                    onChange={(e) =>
                      handleInputChange("contactoEmergencia", e.target.value)
                    }
                    className={clsx(
                      "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                      "placeholder:italic",
                      "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                    )}
                    placeholder="Respuesta"
                  />
                </div>
                <div className="mb-3">
                  <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                    Datos del tutor o responsable
                  </Label>
                  <div className="p-4 border-2 rounded-2xl bg-transparent border-[#bcd6e4]">
                    <div className="mb-3">
                      <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                        Nombre completo
                      </Label>
                      <Input
                        type="text"
                        value={formData.tutorNombre}
                        onChange={(e) =>
                          handleInputChange("tutorNombre", e.target.value)
                        }
                        className={clsx(
                          "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                          "placeholder:italic",
                          "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                        )}
                        placeholder="Respuesta"
                      />
                    </div>
                    <div className="mb-3">
                      <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                        Apellido paterno
                      </Label>
                      <Input
                        type="text"
                        value={formData.tutorApellidoPaterno}
                        onChange={(e) =>
                          handleInputChange(
                            "tutorApellidoPaterno",
                            e.target.value
                          )
                        }
                        className={clsx(
                          "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                          "placeholder:italic",
                          "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                        )}
                        placeholder="Respuesta"
                      />
                    </div>
                    <div className="mb-3">
                      <Label className="text-sm/6 font-semibold text-casal dark:text-gray-200">
                        Apellido materno
                      </Label>
                      <Input
                        type="text"
                        value={formData.tutorApellidoMaterno}
                        onChange={(e) =>
                          handleInputChange(
                            "tutorApellidoMaterno",
                            e.target.value
                          )
                        }
                        className={clsx(
                          "mt-2 block w-full rounded-3xl border-2 bg-white/5 px-3 py-1.5 text-sm/6 dark:text-white text-gray-700",
                          "placeholder:italic",
                          "focus:outline-none focus:ring-2 focus:ring-towerGray focus:border-transparent"
                        )}
                        placeholder="Respuesta"
                      />
                    </div>
                  </div>
                </div>
              </Field>
            </div> */}
          </div>
          <div className="p-3 sm:p-4 border-t-2 absolute bottom-0 w-full bg-white dark:bg-gray-800 rounded-b-xl lg:rounded-bl-xl lg:rounded-br-none shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10">
            <div className="flex flex-row justify-between gap-2">
              <div>
                <Button
                  className={clsx(
                    "bg-gray-300 dark:bg-gray-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-3xl hover:bg-green-500 dark:hover:bg-green-500 transition text-xs sm:text-sm font-medium"
                  )}
                >
                  Editar
                </Button>
              </div>
              <div className="flex flex-row justify-end gap-2">
                <Button
                  className={clsx(
                    "bg-gray-300 dark:bg-gray-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-3xl hover:bg-red-600 dark:hover:bg-red-600 transition text-xs sm:text-sm font-medium"
                  )}
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-casal text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-3xl hover:bg-Acapulco transition text-xs sm:text-sm font-medium disabled:opacity-70"
                  onClick={() => handleGuardarConfiguracion(false)}
                  disabled={guardandoConfig || !eventoActual?.id}
                >
                  {guardandoConfig ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </div>
          </div>
        </div>
        {/* Presentaciones del formulario */}
        <div
          className={clsx(
            "lg:col-span-2 h-[85vh] overflow-y-auto lg:border-l-2 bg-[#5c8287] dark:bg-[#2a9d8f]/5 rounded-r-xl p-4 relative",
            Ocultar ? "lg:col-span-3 rounded-l-xl" : "lg:col-span-2"
          )}
        >
          <div className="absolute top-0 left-0 flex space-x-2 z-10">
            <Button
              onClick={() => setOcultar(!Ocultar)}
              className={clsx(
                "p-2 bg-casal text-white hover:bg-casal/80 transition shadow-sm relative group",
                Ocultar ? "rounded-br-lg rounded-tl-xl " : "rounded-br-lg"
              )}
            >
              {!Ocultar ? (
                <ChevronLeft className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
              {Ocultar ? (
                <span className="absolute left-16 -translate-x-1/2 -bottom-8 z-10 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                  Mostrar formulario
                </span>
              ) : (
                <span className="absolute left-16 -translate-x-1/2 -bottom-8 z-10 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                  Ocultar formulario
                </span>
              )}
            </Button>
          </div>
          <div className="flex-col lg:flex-row lg:space-x-4 lg:space-y-0 space-y-4 flex">
            {/* estos son los detalles del cuestionario pero con los botones se puede hacer para modal celular y laptop */}
            <div className="flex flex-col gap-3 mb-4 lg:hidden">
              {/* Toggle Cuestionario/Contrato Móvil */}
              <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg w-full shadow-inner">
                <button
                  onClick={() => setVistaPrevia("cuestionario")}
                  className={clsx(
                    "flex-1 flex justify-center items-center gap-2 py-1.5 px-3 rounded-md text-xs sm:text-sm font-medium transition-all duration-200",
                    vistaPrevia === "cuestionario"
                      ? "bg-white dark:bg-gray-600 text-casal shadow"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                  )}
                >
                  <ClipboardList className="w-4 h-4" />
                  Formulario
                </button>
                <button
                  onClick={() => setVistaPrevia("contrato")}
                  className={clsx(
                    "flex-1 flex justify-center items-center gap-2 py-1.5 px-3 rounded-md text-xs sm:text-sm font-medium transition-all duration-200",
                    vistaPrevia === "contrato"
                      ? "bg-white dark:bg-gray-600 text-casal shadow"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                  )}
                >
                  <FileText className="w-4 h-4" />
                  Contrato
                </button>
              </div>

              <div className="flex justify-center gap-3">
                <div className="relative group">
                  <Button
                    onClick={() => setModoVista("laptop")}
                    className={clsx(
                      "text-white px-3 py-1 rounded focus:outline-none transition group-hover:shadow-[0_0_10px_2px_rgba(255,255,255,0.4)]",
                      modoVista === "laptop" ? "bg-casal" : "bg-towerGray/70 hover:bg-towerGray"
                    )}
                  >
                    <LaptopIcon className="w-6 h-6" />
                  </Button>
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-8 z-10 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                    Ver en Laptop
                  </span>
                </div>
                <div className="relative group">
                  <Button
                    onClick={() => setModoVista("telefono")}
                    className={clsx(
                      "text-white px-3 py-1 rounded focus:outline-none transition group-hover:shadow-[0_0_10px_2px_rgba(255,255,255,0.4)]",
                      modoVista === "telefono" ? "bg-casal" : "bg-towerGray/70 hover:bg-towerGray"
                    )}
                  >
                    <Smartphone className="w-6 h-6" />
                  </Button>
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-8 z-10 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                    Ver en Teléfono
                  </span>
                </div>
              </div>
              <div className="">
                <Button
                  onClick={handleCopyLink}
                  className="bg-casal w-full text-white px-4 py-1 rounded hover:bg-Acapulco transition"
                >
                  Enlace de evento
                </Button>
              </div>
            </div>
            
            <div className="w-full flex flex-col pt-4 lg:pt-0">
              {/* Toggle Cuestionario/Contrato Escritorio */}
              <div className="hidden lg:flex w-fit mx-auto bg-gray-200 dark:bg-[#1a1a1a] p-1 rounded-lg mb-4 shadow-inner">
                <button
                  onClick={() => setVistaPrevia("cuestionario")}
                  className={clsx(
                    "flex justify-center items-center gap-2 py-1.5 px-6 rounded-md text-sm font-medium transition-all duration-200 border border-transparent",
                    vistaPrevia === "cuestionario"
                      ? "bg-white dark:bg-[#2f2f2f] text-casal shadow-sm dark:border-gray-700"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  )}
                >
                  <ClipboardList className="w-4 h-4" />
                  Vista de Formulario
                </button>
                <button
                  onClick={() => setVistaPrevia("contrato")}
                  className={clsx(
                    "flex justify-center items-center gap-2 py-1.5 px-6 rounded-md text-sm font-medium transition-all duration-200 border border-transparent",
                    vistaPrevia === "contrato"
                      ? "bg-white dark:bg-[#2f2f2f] text-casal shadow-sm dark:border-gray-700"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  )}
                >
                  <FileText className="w-4 h-4" />
                  Vista de Contrato
                </button>
              </div>

              <div
                className={clsx(
                  "flex justify-center items-start transition-all duration-300 mx-auto w-full overflow-hidden"
                )}
              >
                <div 
                  className={clsx(
                    "origin-top transition-all duration-300 flex justify-center",
                    modoVista === "laptop"
                      ? "scale-[0.32] sm:scale-[0.5] md:scale-[0.6] lg:scale-[0.65] xl:scale-90 2xl:scale-100 h-[250px] sm:h-[380px] md:h-[460px] lg:h-[480px] xl:h-[650px] 2xl:h-auto"
                      : "scale-[0.75] sm:scale-90 md:scale-100 h-[640px] sm:h-[760px] md:h-auto"
                  )}
                >
                  <DeviceFrameset
                    device={modoVista === "laptop" ? "MacBook Pro" : "iPhone X"}
                    color={modoVista === "laptop" ? "silver" : "black"}
                    height={modoVista === "laptop" ? 600 : 750}
                    width={modoVista === "telefono" ? 370 : undefined}
                    landscape={modoVista === "laptop"}
                  >
                    <div
                      className={clsx(
                        "h-full overflow-y-auto bg-gray-50 custom-scrollbar",
                        modoVista === "telefono"
                          ? "max-h-[750px] rounded-[2.5rem]"
                          : "max-h-[600px] rounded"
                      )}
                    >
                      {vistaPrevia === "cuestionario" ? (
                        <FormularioCuestionario
                          modoVista={modoVista}
                          eventoData={eventoActual}
                          formData={formData}
                          configuracion={configuracionCuestionario}
                        />
                      ) : (
                        <PreviewContrato 
                          eventoData={eventoActual} 
                          modoVista={modoVista} 
                        />
                      )}
                    </div>
                  </DeviceFrameset>
                </div>
              </div>
            </div>
            {/* botones para cambiar vista Modo*/}
            <div className="w-full lg:w-40 lg:space-y-3 flex lg:flex-col gap-3 lg:block hidden">
              <div className="flex justify-center gap-3">
                <div className="relative group">
                  <Button
                    onClick={() => setModoVista("laptop")}
                    className={clsx(
                      "text-white px-3 py-1 rounded",
                      "focus:outline-none transition",
                      modoVista === "laptop" ? "bg-Acapulco" : "bg-casal"
                    )}
                  >
                    <LaptopIcon className="w-6 h-6" />
                  </Button>
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-8 z-10 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                    Vista laptop
                  </span>
                </div>
                <div className="relative group">
                  <Button
                    onClick={() => setModoVista("telefono")}
                    className={clsx(
                      "text-white px-3 py-1 rounded",
                      "focus:outline-none transition",
                      modoVista === "telefono" ? "bg-Acapulco" : "bg-casal"
                    )}
                  >
                    <Smartphone className="w-6 h-6" />
                  </Button>

                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-8 z-10 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition">
                    Vista teléfono
                  </span>
                </div>
              </div>
              <div className="">
                <Button
                  onClick={handleCopyLink}
                  className="bg-casal  w-full text-white px-4 py-1 rounded hover:bg-Acapulco transition"
                >
                  Enlace de cuestionario
                </Button>
              </div>
            </div>
          </div>
        </div>
        <ModalConfimacion
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      </div>
    </div>
  );
}
